/**
 * Labor standards — pure helpers shared by the catalog, the form, the
 * preview dialog and the upload review.
 */
import {
	LaborStandard,
	LaborStandardOperation,
	LaborStandardOperationInput,
} from "../../../../services/labor-standards-api";
import { planner } from "../../planner-ui";
import { isMachinePhase, machinePhaseLabel } from "../../utils/standard-matching";

/** "45 min", "2h", "2h 30m" — how this page writes a duration. */
export const formatDuration = (minutes: number): string => {
	if (minutes < 60) return `${minutes} min`;
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

export const operationLabel = (operation: LaborStandardOperation): string =>
	operation.name || operation.operationTypeName || operation.operationType?.name || "Operation";

// Category is shown only when it adds information — when a standard has no
// per-operation name the two strings are identical and repeating it is noise.
export const operationCategory = (operation: LaborStandardOperation): string => {
	const category = operation.operationTypeName || operation.operationType?.name || "";
	return category && category !== operationLabel(operation) ? category : "";
};

// Sentinel for the "start a fresh group" option in the Runs picker.
export const NEW_PARALLEL_GROUP = "__new__";

// Groups are stored as integers but read better as letters in the UI.
export const parallelGroupLabel = (group: number): string =>
	group >= 1 && group <= 26 ? String.fromCharCode(64 + group) : String(group);

export const nextParallelGroupId = (operations: { parallelGroup?: number | null }[]): number =>
	operations.reduce((max, op) => Math.max(max, op.parallelGroup ?? 0), 0) + 1;

// The customer's own work-type vocabulary for a phase ("100 Installation"),
// when the phase is linked to one of the company's configured work phases.
export const workPhaseLabel = (phase: { workPhaseCode?: string; workPhaseName?: string }): string =>
	[phase.workPhaseCode, phase.workPhaseName].filter(Boolean).join(" ");

// Derive role -> total labor minutes (duration x workers) from a standard's
// LABOR phases. Machine time (zero workers) holds the unit but is not labor,
// so it never appears in the role bar or legend.
export const getRoleBreakdown = (standard: LaborStandard): [string, number][] => {
	const totals: Record<string, number> = {};
	(standard.operations || []).forEach((op) => {
		(op.phases || []).forEach((phase) => {
			if (isMachinePhase(phase)) return;
			const minutes = (phase.durationMinutes || 0) * phase.workersRequired;
			totals[phase.jobRole] = (totals[phase.jobRole] || 0) + minutes;
		});
	});
	return Object.entries(totals).sort((a, b) => b[1] - a[1]);
};

// "Machine · 8h" — the work phase's own name when the line has one.
export const machineLineLabel = (phase: { workPhaseName?: string | null }, minutes: number): string =>
	`${machinePhaseLabel(phase)} · ${formatDuration(minutes)}`;

// Machine time on a timeline: the unit is busy but nobody is needed. Hatched
// alloy on a hairline so it reads as OCCUPANCY, never as a crew.
export const machineBarSx = {
	backgroundColor: planner.surfaceSubtle,
	backgroundImage: `repeating-linear-gradient(135deg, transparent 0 6px, ${planner.hairline} 6px 8px)`,
	border: `1px solid ${planner.hairlineStrong}`,
	color: planner.inkSecondary,
} as const;

// Crew size as typed in the form: "" is unset, anything else must be a finite
// number >= 0 (0 = machine time; 0.5 / 2.67 are real fractional crews).
export const parseCrew = (raw: string): number | null => {
	if (raw.trim() === "") return null;
	const value = Number(raw);
	return Number.isFinite(value) && value >= 0 ? value : null;
};

// ---- The form's shape -----------------------------------------------------------

export interface PhaseFormRow {
	jobRoleId: number | null;
	jobRole: string;
	/** The company's work phase this line is (its "phase type"). */
	workPhaseId: number | null;
	/** As typed; "0" = machine time, decimals allowed. */
	workersRequired: string;
	startOffsetMinutes: string;
	durationMinutes: string;
	/** Phase may be scheduled later if nobody qualified is free. */
	isDeferrable?: boolean;
	notes: string;
}

export interface WorkstationOptionFormRow {
	workstation: string;
	durationMinutes: string;
}

export interface OperationFormRow {
	name: string;
	operationTypeId: string;
	/** The work center this step runs on. Required — every step runs somewhere. */
	workstation: string;
	/** Alternative work centers, each with an optional duration override (minutes). */
	workstationOptions: WorkstationOptionFormRow[];
	/**
	 * Operations sharing this group id run CONCURRENTLY. They need not be
	 * adjacent — the id is the grouping, not the row position.
	 * Null/undefined = runs on its own (sequential default).
	 */
	parallelGroup?: number | null;
	notes: string;
	phases: PhaseFormRow[];
}

export interface StandardFormData {
	/** e.g. SAP group number, "MAIN", "ALT". Blank = the product's only recipe. */
	recipeGroup: string;
	batchSize: string;
	batchUnit: string;
	description: string;
	operations: OperationFormRow[];
}

export const EMPTY_FORM: StandardFormData = {
	recipeGroup: "",
	batchSize: "",
	batchUnit: "",
	description: "",
	operations: [],
};

// A blank phase row: one worker by default, no phase type, no role yet.
export const emptyPhaseRow = (): PhaseFormRow => ({
	jobRoleId: null,
	jobRole: "",
	workPhaseId: null,
	workersRequired: "1",
	startOffsetMinutes: "0",
	durationMinutes: "60",
	isDeferrable: false,
	notes: "",
});

/** A saved standard as the form edits it. */
export const standardToForm = (standard: LaborStandard, formatCrew: (workers: number) => string): StandardFormData => ({
	recipeGroup: standard.recipeGroup || "",
	batchSize: standard.batchSize.toString(),
	batchUnit: standard.batchUnit || "",
	description: standard.description || "",
	operations: (standard.operations || []).map((op) => ({
		name: op.name || "",
		operationTypeId: op.operationTypeId.toString(),
		// A legacy operation without its own work center inherits the header's
		workstation: op.workstation || standard.workstation || "",
		workstationOptions: (op.workstationOptions || []).map((option) => ({
			workstation: option.workstation,
			durationMinutes: option.durationMinutes != null ? String(option.durationMinutes) : "",
		})),
		parallelGroup: op.parallelGroup ?? null,
		notes: op.notes || "",
		phases: (op.phases || []).map((phase) => ({
			jobRoleId: phase.jobRoleId ?? null,
			jobRole: phase.jobRole,
			workPhaseId: phase.workPhaseId ?? null,
			workersRequired: formatCrew(phase.workersRequired),
			startOffsetMinutes: phase.startOffsetMinutes.toString(),
			durationMinutes: phase.durationMinutes.toString(),
			isDeferrable: Boolean(phase.isDeferrable),
			notes: phase.notes || "",
		})),
	})),
});

/** The first thing wrong with the form, or null when it can be saved. */
export const validateForm = (form: StandardFormData, hasProduct: boolean): string | null => {
	if (!hasProduct) return "No product selected";
	if (!form.batchSize || parseInt(form.batchSize) <= 0) return "Please enter a valid batch size";
	if (form.operations.length === 0) return "Please add at least one operation";

	for (let opIdx = 0; opIdx < form.operations.length; opIdx++) {
		const op = form.operations[opIdx];
		if (!op.operationTypeId) return `Operation ${opIdx + 1}: Please select an operation type`;
		if (!op.workstation) return `Operation ${opIdx + 1}: Please select the work center this step runs on`;
		if (op.phases.length === 0) return `Operation ${opIdx + 1}: Please add at least one phase`;

		for (let phaseIdx = 0; phaseIdx < op.phases.length; phaseIdx++) {
			const phase = op.phases[phaseIdx];
			const crew = parseCrew(phase.workersRequired);
			if (crew === null) {
				return `Operation ${opIdx + 1}, Phase ${phaseIdx + 1}: Please enter the number of workers (0 for machine time)`;
			}
			// Machine time (0 workers) needs no job role; a crew does.
			if (crew > 0 && (!phase.jobRoleId || !phase.jobRole)) {
				return `Operation ${opIdx + 1}, Phase ${phaseIdx + 1}: Please select a job role`;
			}
			if (!phase.durationMinutes || parseInt(phase.durationMinutes) <= 0) {
				return `Operation ${opIdx + 1}, Phase ${phaseIdx + 1}: Please enter a valid duration`;
			}
		}
	}
	return null;
};

/** The operations as the API wants them. The server recomputes durations. */
export const formToOperations = (form: StandardFormData): LaborStandardOperationInput[] => {
	// A group of one is not a group: drop ids that ended up with a single
	// member (the planner moved the other operation out) so the row stores
	// null and reads as plain sequential.
	const groupMemberCount = new Map<number, number>();
	form.operations.forEach((op) => {
		if (op.parallelGroup != null) {
			groupMemberCount.set(op.parallelGroup, (groupMemberCount.get(op.parallelGroup) || 0) + 1);
		}
	});

	return form.operations.map((op, opIndex) => ({
		// Left unset when blank so the server falls back to the type name.
		name: op.name.trim() || undefined,
		operationTypeId: parseInt(op.operationTypeId),
		sequence: opIndex + 1,
		parallelGroup:
			op.parallelGroup != null && (groupMemberCount.get(op.parallelGroup) || 0) > 1 ? op.parallelGroup : null,
		workstation: op.workstation,
		workstationOptions: op.workstationOptions
			.filter((option) => option.workstation && option.workstation !== op.workstation)
			.map((option) => ({
				workstation: option.workstation,
				durationMinutes: option.durationMinutes ? parseInt(option.durationMinutes) || null : null,
				isDefault: false,
			})),
		durationMinutes: 0, // Will be calculated by backend
		notes: op.notes || undefined,
		phases: op.phases.map((phase, phaseIndex) => ({
			jobRoleId: phase.jobRoleId && phase.jobRoleId > 0 ? phase.jobRoleId : null,
			jobRole: phase.jobRole,
			workPhaseId: phase.workPhaseId ?? null,
			// As entered: 0 is machine time, 0.5 / 2.67 are fractional crews.
			workersRequired: parseCrew(phase.workersRequired) ?? 0,
			sequence: phaseIndex + 1,
			startOffsetMinutes: parseInt(phase.startOffsetMinutes) || 0,
			durationMinutes: parseInt(phase.durationMinutes),
			isDeferrable: Boolean(phase.isDeferrable),
			notes: phase.notes || undefined,
		})),
	}));
};
